import React from 'react';
import { isImageSource } from '../utils/shopImages';
import { bannerSurface } from '../theme';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withStyles from '../withStylesAdapter';
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import 'react-virtualized/styles.css'; // only needs to be imported once
import IconButton from '@mui/material/IconButton';
import RichTextEditor from '../components/RichTextEditor'
import { markupIssues } from '../markupCheck';
import ImageField from './ImageField'
import CollapsableFields from './CollapsableFields'
import CustomFields from './CustomFields'
import SeoFields from './SeoFields'
import { updateAutoTranslations } from '../modules/shop'
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import BlockIcon from '@mui/icons-material/Block';
import TranslateIcon from '@mui/icons-material/Translate';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Chip from '@mui/material/Chip';
import { useTranslation, withTranslation } from 'react-i18next';
import DontAskAgainDialog from './DontAskAgainDialog.js';
import JsonEditor from './JsonEditor';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { ChevronRightOutlined } from '@mui/icons-material';
import { event } from 'jquery';
import SourceToTargetMenu from './SourceToTargetMenu';

const REACT_APP_ENABLE_AUTO_TRANSLATIONS = window.loadEnv("REACT_APP_ENABLE_AUTO_TRANSLATIONS", process.env.REACT_APP_ENABLE_AUTO_TRANSLATIONS);

/**
 * Height for a field that asked for a lot of rows - a packing slip or a notification body,
 * where the content is a whole document and a three line box is unusable.
 *
 * It has to be minHeight rather than height: MUI's autosize writes an inline `height`, and
 * the `inputField` class overrides that with `height: auto !important`, so the textarea
 * falls back to its rows attribute and collapses. minHeight is a different property, so it
 * survives - the same reason the richtext editor sets one.
 *
 * Only for fields well past the default, so ordinary multiline fields are untouched.
 */
const TALL_FIELD_MIN_ROWS = 10;
const tallFieldStyle = ({ multiline, rows }) =>
  (multiline && rows >= TALL_FIELD_MIN_ROWS) ? { minHeight: rows * 19 } : undefined;

// These two did save, just under an adjusted handle - there is nothing left to restore
// the backend reports these as validation errors although it saved a value: the handle was
// simplified to what Shopify accepts. The saved value is the truth, not what was sent
export const ADJUSTED_ERROR_KEYS = ['error_handle_adjusted', 'error_handle_taken_and_adjusted'];

// a field whose only complaint is such an adjustment - its incoming target is the corrected value
export const hasAdjustedValue = (field) => {
  const validationErrors = field && field.errors && field.errors.validation_errors;
  if(!validationErrors) return false;
  const keys = Object.keys(validationErrors);
  return keys.length > 0 && keys.every(key => ADJUSTED_ERROR_KEYS.includes(key));
};

class TranslationField extends React.Component {
  hasMounted = false;
  mouseIsOver = false;

  constructor(props) {
    super(props);

    this.state = {
      usedText: props.target,
      error: props.error,
      error_message: '',
      hadError: false,
      actionsMenuAnchor: null,
      // alt text rows (paired with an image): both text areas take the taller one's height
      pairedMinHeight: null,
      // the JSON editor reports whether the source is a JSON structure; the menu opens it by counter
      jsonEditorAvailable: false,
      jsonEditorOpenRequest: 0,
      // sourcePlaceholder fields show the source greyed in the empty target; it leaves on focus
      targetFocused: false,
    }
  }

  componentDidMount = () => {
    this.hasMounted = true;

    if(this.props.onLoaded) {
      setTimeout(() => {
        if(this.hasMounted) {
          this.props.onLoaded();
        }
      }, 100);      
    }
    if(this.props.imageId) {
      this.syncPairedHeight();
      // a narrower column wraps the text differently, so the pair is measured again
      window.addEventListener('resize', this.syncPairedHeight);
    }
  }

  componentWillUnmount = () => {
    this.hasMounted = false;
    if(this.props.imageId) window.removeEventListener('resize', this.syncPairedHeight);
  }

  componentWillReceiveProps(newProps) {
    const wasPending = this.props.isPending;
    const isPending = newProps.isPending;

    if(wasPending === true && isPending === false) {
      this.setState({
        //hadError: false,
      });
    }

    if(wasPending === true && isPending === false || newProps.target !== this.state.usedText) {
      this.setState({
        usedText:newProps.target,
      });
    }

    // only a field that really changed reports on the pulse: an untouched field has no initial, and
    // reporting it registered an edit with an empty restore value - a later discard then emptied
    // every cell of the item (seen after clearing one field and running a machine translation)
    if(newProps.forceUpdate === false && this.props.forceUpdate === true && newProps.initial !== undefined && newProps.initial !== newProps.target) {
      let data = {
        id: this.props.id,
        changed: true,
        initial: newProps.initial,
        target: newProps.target,
        parentField: newProps.parentField,
      }
      this.handleChange(null, data);
    }

    if(newProps.error !== this.props.error) {
      this.setState({
        error: newProps.error,
      });
    }
  }

  componentDidUpdate(prevProps) {
    // only the save errors feed initErrors; hashing all props stringified the i18n instance (every
    // locale file) twice per row on each update, seconds per keystroke in a long list
    if(JSON.stringify(this.props.errors ?? null) !== JSON.stringify(prevProps.errors ?? null)) {
      this.initErrors();
    }
    this.syncPairedHeight();
  }

  handleChange = (event, data) => {
    let passThroughData = !event ? data : {
      id: this.props.id,
      changed: this.props.target != event.target.value,
      initial: this.props.target,
      target: event.target.value,
      error: false,
    };
    this.setState({
      usedText: !event ? data.target : event.target.value,
      //hadError: this.props.error ? true : false,
    });
    this.props.onChange(event, passThroughData);
  };

  selectField = (itemId, fieldId, parentField) => {
    if(parentField === undefined) {
      parentField = this.props.parentField;
    }
    if(parentField === 'json-editor') {
      return;
    }

    let hash = fieldId;

    if(parentField) {
      hash = parentField + ':' + hash;
      if(this.props.onSubfieldSelected) {
        this.props.onSubfieldSelected(itemId, fieldId, parentField);
      }
    } else if(this.props.onFieldSelected) {
      this.props.onFieldSelected(itemId, fieldId);
    }

    window.location.hash = hash;
  }

  // The pre-edit value of every touched field lives in window.restoreFieldsData under
  // `${itemId}[-${parentField}]-${fieldId}` - ItemsList writes it, this reads it back.
  // Returns null when there is nothing stored, which is not the same as a stored ''.
  _getRestoreValue = () => {
    const store = window.restoreFieldsData || {};
    const key = this.props.parentField
      ? `${this.props.itemId}-${this.props.parentField}-${this.props.id}`
      : `${this.props.itemId}-${this.props.id}`;
    return key in store ? store[key] : null;
  }

  // Handle errors never reach the saveErrors channel, they arrive as a field-level `errors`
  // object - so a rejected handle has no `error` flag to go by
  _saveFailed = () => {
    if(this.props.error) {
      return true;
    }
    const validationErrors = this.props.errors && this.props.errors.validation_errors;
    return !!validationErrors && Object.keys(validationErrors).some(key => !ADJUSTED_ERROR_KEYS.includes(key));
  }

  _canRestore = () => {
    const restoreValue = this._getRestoreValue();
    return this._saveFailed() && restoreValue !== null && restoreValue !== (this.state.usedText || '');
  }

  _renderTargetHelper = () => this._renderRestoreLink() || null;

  // A MUI Link so it carries the same blue as every other text action in the app instead of
  // the red it inherited from the error helper text
  _renderRestoreLink = () => {
    if(!this._canRestore()) {
      return null;
    }
    return (
      <Link component="span" underline="hover" onClick={this.handleRestore} className={this.props.classes.restoreLink}>
        <ReplayRoundedIcon style={{marginTop: -2, fontSize: 14}} /> Restore value
      </Link>
    );
  }

  handleRestore = (event) => {
    const restoreValue = this._getRestoreValue();
    if(restoreValue === null) {
      return;
    }

    // The richtext editor keeps its own DOM content and only re-reads it on a forceUpdate,
    // so a plain state change would leave the rejected value on screen
    this.setState({
      usedText: restoreValue,
    }, this.handleTriggerForceUpdate)

    // The entry stays: onChange lands in ItemsList.handleChangeText, which drops it itself
    // once the field matches its pre-edit value again
    this.props.onChange(null, {
      id: this.props.id,
      changed: false,
      initial: restoreValue,
      target: restoreValue,
      parentField: this.props.parentField,
    })
  }

  hasErrors() {
    if (typeof this.props.errors !== 'undefined' && this.props.errors !== false) return true;
    return false;
  }

  // Shopify rejects a handle translation that equals the original, so the sections send '' for it
  // and the value silently vanishes on save; compared case-insensitively, handles are lowercased
  _handleEqualsSource = () => {
    if(this.props.id !== 'handle' || typeof this.props.source !== 'string') {
      return false;
    }
    const target = (this.state.usedText || '').trim();
    return target !== '' && target.toLowerCase() === this.props.source.trim().toLowerCase();
  }

  // plain text fields with HTML or Liquid (theme editor content): unbalanced tags in the translation,
  // formatting lost, placeholders that differ from the original. Judged on the saved translation,
  // not the text being typed - nobody types closed markup from the first character
  _markupIssues = () => {
    // theme only: elsewhere the rich text editor owns the HTML, and JSON structures have their own view
    if(this.props.context !== 'theme' || this.props.type !== 'text' || this.state.jsonEditorAvailable) return null;
    // props.target follows the typing; the saved value of a touched field is its restore value
    const restoreValue = this._getRestoreValue();
    return markupIssues(this.props.source, restoreValue !== null ? restoreValue : this.props.target);
  }

  initErrors() {
    const { t } = this.props;
    if(this.props.errors && this.props.errors.validation_errors) {
      let reason = [];
      Object.keys(this.props.errors.validation_errors).forEach(key => {
        switch(key) {
          case "error_handle_taken" : 
            reason.push(t('itemsList.errors.handleHasBeenTaken', { object_value: this.props.errors.object_value }));
            break;
          case "error_handle_adjusted" :
            reason.push(t('itemsList.errors.errorHandleAdjusted', { object_value: this.props.errors.object_value }));
            break;
          case "error_handle_taken_and_adjusted" :
            reason.push(t('itemsList.errors.handleHasBeenTakenAndAdjusted', { object_value: this.props.errors.object_value }));
            break;
        }
      })
      this.setState({
        error_message: reason.join(' - '),
      });
    } else if (this.state.error_message !== '') {
      this.setState({
        error_message: ''
      });
    }
  }

  handleTriggerForceUpdate = (event) => {
    this.setState({
      forceUpdate: true
    }, () => {
      this.setState({
        forceUpdate: false
      })
    })
  }

  currentMode = () => this.props.translationMode;

  // the two auto-growing text areas of an alt row size themselves to their own text; after every
  // render the shorter one is lifted to the taller one's natural height. The inline height the
  // autosize sets and our min-height are both collapsed for the measurement: scrollHeight never
  // reads below the box's current height, so with them in place a text that lost lines kept
  // measuring at its old height and the pair never shrank back
  syncPairedHeight = () => {
    if(!this.props.imageId || !this.sourceInputEl || !this.targetInputEl) return;
    const natural = (el) => {
      const prevMin = el.style.minHeight;
      const prevHeight = el.style.height;
      el.style.minHeight = '0px';
      el.style.height = '0px';
      const h = el.scrollHeight;
      el.style.minHeight = prevMin;
      el.style.height = prevHeight;
      return h;
    };
    const height = Math.max(natural(this.sourceInputEl), natural(this.targetInputEl));
    if(height > 0 && height !== this.state.pairedMinHeight) {
      this.setState({ pairedMinHeight: height });
    }
  }

  // empties the target like typing would: a local edit until the item is saved, so no dialog.
  // The rich text editor re-reads its content on the force update pulse
  handleClearTranslation = () => {
    this.handleCloseActionsMenu();
    this.handleChange(null, {
      id: this.props.id,
      changed: this.props.target !== '',
      initial: this.props.target,
      target: '',
      parentField: this.props.parentField,
    });
    this.handleTriggerForceUpdate();
  }

  // a plain action: the menu closes, the field shows the new mode
  handleModeChange = (mode) => {
    this.handleCloseActionsMenu();
    if (this.props.onTranslationModeChange) {
      this.props.onTranslationModeChange(this.props.id, mode, this.props.parentField);
    }
  }

  renderFieldMenuButton = () => (
    <div style={{ position: "absolute", top: 6, right: "-8px" }}>
      <Tooltip title={this.props.t('itemsList.fieldMenu')} arrow>
        <IconButton aria-label={this.props.t('itemsList.fieldMenu')} onClick={this.handleOpenActionsMenu} size="small">
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
    </div>
  )

  // only sections that opt in show the flag; other sections carry Shopify's outdated flag, which a
  // retranslation with the same text would not clear
  isOutdated = () => this.props.flagsOutdated === true && this.props.outdated === true;

  // the field menu: import / machine translation (SourceToTargetMenu), the mode switch, the
  // delete entries and a read-only status block. Image rows get neither import nor machine
  // translation - the image dialog covers that - so they use the plain menu
  renderFieldMenu = (type) => {
    const { t, deletable, translationDeletable, hideSourceToTargetMenu } = this.props;
    // a target equal to its source is no translation (the save drops it, an image shows the original)
    const hasTarget = this.state.usedText !== undefined && this.state.usedText !== null && this.state.usedText !== '' && this.state.usedText !== this.props.source;
    const statusKey = !hasTarget ? 'itemsList.fieldStatusEmpty' : (this.isOutdated() ? 'itemsList.fieldStatusOutdated' : 'itemsList.fieldStatusTranslated');
    const mode = this.currentMode();
    const blocked = mode === 'block';
    const extras = [];
    // glossary terms: one entry that switches to the other mode and says what that means. A
    // blocked term has no translation status, so the status block stays out
    if(mode) {
      extras.push(
        <MenuItem key="mode" dense onClick={() => this.handleModeChange(blocked ? 'translate' : 'block')}>
          <ListItemIcon>{blocked ? <TranslateIcon fontSize="small" /> : <BlockIcon fontSize="small" />}</ListItemIcon>
          <ListItemText
            primary={t(blocked ? 'itemsList.fieldModeToTranslate' : 'itemsList.fieldModeToBlock')}
            secondary={t(blocked ? 'itemsList.fieldModeToTranslateHint' : 'itemsList.fieldModeToBlockHint')}
          />
        </MenuItem>
      );
    }
    // an outdated translation the reviewer accepts as it is: saved as current without a text change
    if(hasTarget && !blocked && this.isOutdated() && this.props.onKeepTranslation) {
      extras.push(
        <MenuItem key="keep" dense onClick={this.handleKeepTranslation}>
          <ListItemIcon><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('itemsList.fieldKeepTranslationBtn')} secondary={t('itemsList.fieldKeepTranslationHint')} />
        </MenuItem>
      );
    }
    if(hasTarget && !blocked) {
      extras.push(
        <MenuItem key="clear" dense onClick={this.handleClearTranslation}>
          <ListItemIcon><BackspaceOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('imageField.clearTranslationBtn')}</ListItemText>
        </MenuItem>
      );
    }
    // the milder action first: a slip lands on the translation, not on the field
    if(translationDeletable) {
      extras.push(
        <MenuItem key="delete-translation" dense onClick={this.handleDeleteTranslation}>
          <ListItemIcon><BackspaceOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('itemsList.fieldDeleteTranslationBtn')}</ListItemText>
        </MenuItem>
      );
    }
    if(deletable) {
      extras.push(
        <MenuItem key="delete-field" dense onClick={this.handleDeleteField}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('itemsList.fieldDeleteBtn')}</ListItemText>
        </MenuItem>
      );
    }
    if(!blocked) {
      if(extras.length > 0) {
        extras.push(<Divider key="info-divider" />);
      }
      const outdated = this.isOutdated() && hasTarget;
      const StatusIcon = !hasTarget ? RadioButtonUncheckedIcon : (outdated ? ErrorOutlineIcon : CheckCircleOutlineIcon);
      const statusColor = !hasTarget ? 'text.disabled' : (outdated ? 'warning.main' : 'success.main');
      extras.push(
        <ListSubheader key="status" disableSticky sx={{ lineHeight: '28px' }}>{t('itemsList.fieldStatus')}</ListSubheader>,
        <Typography key="status-value" component="div" variant="body2" sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1, maxWidth: 320, color: outdated ? 'warning.main' : 'text.secondary' }}>
          <StatusIcon fontSize="small" sx={{ color: statusColor, flexShrink: 0 }} />
          <span>{t(statusKey)}</span>
        </Typography>
      );
    }

    // JSON metafields: the formatted view is a third way of editing the target, so it joins
    // import / machine translation above the divider rather than the status entries below
    const editItems = (this.state.jsonEditorAvailable && this.props.parentField !== 'json-editor') ? [
      <MenuItem key="json-editor" dense onClick={() => { this.handleCloseActionsMenu(); this.setState({ jsonEditorOpenRequest: this.state.jsonEditorOpenRequest + 1 }); }}>
        <ListItemIcon><MenuOpenRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('itemsList.translateJsonObject')}</ListItemText>
      </MenuItem>
    ] : null;

    if(hideSourceToTargetMenu || type === 'image') {
      return (
        <Menu
          anchorEl={this.state.actionsMenuAnchor}
          open={Boolean(this.state.actionsMenuAnchor)}
          onClose={this.handleCloseActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {editItems}
          {editItems && extras.length > 0 && <Divider />}
          {extras}
        </Menu>
      );
    }
    return (
      <SourceToTargetMenu
        variant="menu"
        editItems={editItems}
        anchorEl={this.state.actionsMenuAnchor}
        onClose={this.handleCloseActionsMenu}
        data={this.props}
        from={this.props.from}
        to={this.props.to}
        // through handleChange: the field's own value has to change before the force update re-reads it
        onChange={this.handleChange}
        onTriggerForceUpdate={this.handleTriggerForceUpdate}
        parentField={this.props.parentField}
      >
        {extras}
      </SourceToTargetMenu>
    );
  }

  handleOpenActionsMenu = (event) => {
    event.stopPropagation();
    this.setState({ actionsMenuAnchor: event.currentTarget });
  }

  handleCloseActionsMenu = () => {
    this.setState({ actionsMenuAnchor: null });
  }

  handleDeleteField = () => {
    this.handleCloseActionsMenu();
    this.setState({ showDeleteNotice: true });
  }

  handleKeepTranslation = () => {
    this.handleCloseActionsMenu();
    this.props.onKeepTranslation(this.props.id, this.props.parentField);
  }

  handleDeleteTranslation = () => {
    this.handleCloseActionsMenu();
    if (this.props.onDeleteTranslation) {
      this.props.onDeleteTranslation(this.props.id, this.props.parentField);
    }
  }

  render() {
    let root = this;

    const {
      loadingTranslations,
      loadingTranslationsError,
      //hadError,
    } = this.state;
    const {
      t,
      classes,
      id,
      itemId,
      multiline,
      fields,
      deletable,
      translationDeletable,
      to,
      source,
      target,
      saveErrors,
      parentField,
      paginated,

      planRestricted,
      hideSourceToTargetMenu
    } = this.props;

    if(this.props.isHidden) {
      return null
    }

    // group headers of an item (collapsable subfield blocks)
    const groupLabelKeys = {
      seofields: 'itemsList.seoFieldsGroup',
      variants: 'itemsList.variants',
      option_names: 'itemsList.optionNames',
      metafields: 'itemsList.metafields',
      variant_metafields: 'itemsList.variantMetafields',
      images: 'itemsList.images',
      customfields: 'itemsList.customFields',
    };
    const usedLabel = groupLabelKeys[id] ? this.props.t(groupLabelKeys[id]) : '';

    const truncate = (input, len) => input.length > len ? `${input.substring(0, len)}...` : input;
    const fromLanguage = this.props.languages.languages.find(x => x.base == true);
    const toLanguage = this.props.languages.languages.find(x => x.id == this.props.shop.shop.edit_language);



    const getEditorVersion = () => {
      //return 'v2';
      if(this.props.shop.shop.langify_features && this.props.shop.shop.langify_features.find(x => x.name === 'editor_v2') && this.props.shop.shop.langify_features.find(x => x.name === 'editor_v2').is_enabled === true) {
        return 'v2';
      } else {
        return 'v1';
      }
    }

    let type = this.props.type;
    if(typeof source === 'string' && this.props.shop.shop.langify_features && this.props.shop.shop.langify_features.find(x => x.name === 'html_text_2_rich_text') && this.props.shop.shop.langify_features.find(x => x.name === 'html_text_2_rich_text').is_enabled === true) {
      const htmlContent = source.match(/^<(p|h1|h2|h3|h4|h5|div|span|ul|script).*>(.*)<\/(p|h1|h2|h3|h4|h5|div|span|ul|script)>$/gim);
      if(type === 'text' && htmlContent && htmlContent.length > 0) {
        type = 'richtext';
      }
    }
    // theme settings and metafields deliver image URLs as plain text - render those as image rows.
    // presentation only: the field stays a text field on the save path, like the html sniff above
    if(type === 'text' && isImageSource(source)) {
      type = 'image';
    }
    // every text/richtext row carries the field menu, its button sits right of the target; the row
    // reserves the room for it (the delete entries live in the menu too)
    // image rows get the menu too, reduced to the status (and delete entries): see renderFieldMenu
    const hasFieldMenu = !fields && type !== 'subfields' && parentField !== 'json-editor';
    const inJsonEditor = parentField === 'json-editor';
    // the arrow carries the hover menu with import / machine translation; the JSON view rows have
    // no kebab and only reach these actions through the arrow
    const hasArrowMenu = (hasFieldMenu || inJsonEditor) && !hideSourceToTargetMenu;
    // rows without a menu (images, subfields) reserve the same room, so the columns line up
    const rightPadding = inJsonEditor ? "0px": "28px";
    const rightArrowMargin = inJsonEditor ? "11px": "9px";
    const leftArrowMargin = inJsonEditor ? "inherit": "-14px";

    const open = Boolean(this.state.anchorEl);

    return (
      <React.Fragment>
        <React.Fragment>
          {/* the confirm for the menu's delete entry; outside the row branches, so a custom
              content rendered as an image row (URL source) gets it too */}
          {deletable === true &&
            <DontAskAgainDialog
              context={this.props.context ? `Delete-${this.props.context}-Content` : 'DeleteCustomContent'}
              open={this.state.showDeleteNotice}
              onClose={() => { this.setState({ showDeleteNotice: false }); } }
              onCancel={() => { this.setState({ showDeleteNotice: false }); } }
              onSubmit={e => { this.setState({ showDeleteNotice: false }); this.props.onDeleteItem(this.props.id); } }
              title={t('customContents.deleteNoticeTitle', {context: this.props.context || 'custom'})}
              content={t('customContents.deleteNoticeDescription', {context: this.props.context || 'custom'})}
              initialChecked={false} />
          }
          {type === 'image' &&
            <Grid container columnSpacing={3} rowSpacing={{ xs: 0, md: 3 }} style={{paddingRight:rightPadding}} data-field-id={id} className={classNames('field-row', classes.root, `${type}`, this.props.hasAlt && 'has-alt', this.props.multiline && 'compact', parentField === 'images' && 'grouped')}>
              <span
                className={classes.arrow}
                style={{
                  zIndex:"1",
                  marginRight: rightArrowMargin,
                  marginLeft: leftArrowMargin,
                  // the class offset (-12px) is tuned for text rows; the image box centers plainly
                  top: '50%'
                }}>
                <ChevronRightOutlined />
              </span>
              {hasFieldMenu && this.renderFieldMenuButton()}
              {hasFieldMenu && this.renderFieldMenu(type)}
              <Grid size={{ xs: 12, md: 6 }} className={classes.gridOdd} onClick={() => this.selectField(itemId, id)}>
                <ImageField
                  variant="source"
                  label={this.props.label}
                  mediaGid={this.props.mediaGid}
                  rows={this.props.multiline ? this.props.rows : undefined}
                  source={this.props.source}
                  target={this.state.usedText ? this.state.usedText : ''}
                  itemId={this.props.id}
                  large={this.props.largeImages}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} className={classes.gridEven} onClick={() => this.selectField(itemId, id)}>
                <ImageField
                  variant="target"
                  rows={this.props.multiline ? this.props.rows : undefined}
                  source={this.props.source}
                  target={this.state.usedText ? this.state.usedText : ''}
                  itemId={this.props.id}
                  isPending={this.props.isPending}
                  onChange={this.handleChange}
                  navigation={this.props.imageNavigation
                    ? { ...this.props.imageNavigation, onNavigate: (direction, view) => this.props.onImageNavigate(this.props.id, direction, view) }
                    : null}
                  autoOpen={this.props.openImageDialog === true}
                  autoOpenView={this.props.openImageDialogView}
                  onAutoOpened={this.props.onImageDialogOpened}
                  large={this.props.largeImages}
                  broken={this.props.imageBroken === true}
                  highlighted={id == this._getFieldIdByHash()}
                />
              </Grid>
            </Grid>
          }
          {(type !== 'subfields' && type !== 'image') &&
            <Grid container columnSpacing={3} rowSpacing={{ xs: 0, md: 3 }} style={{paddingRight:rightPadding}} data-field-id={id} className={classNames('field-row', classes.root, `${type}`, planRestricted && 'plan-restricted', this.props.imageId && 'alt-paired')}>
              {!fields && hasArrowMenu &&
                <SourceToTargetMenu
                  data={this.props}
                  from={this.props.from}
                  to={this.props.to}
                  onChange={this.handleChange}
                  onTriggerForceUpdate={this.handleTriggerForceUpdate}
                  parentField={this.props.parentField}
                  // the class offset (-12px) balances the list's row spacing; the JSON view has none
                  arrowStyle={{ marginRight: rightArrowMargin, marginLeft: leftArrowMargin, top: inJsonEditor ? '50%' : undefined }}
                />
              }
              {!fields && !hasArrowMenu &&
                <span
                  className={classes.arrow}
                  data-tour="source-to-target-arrow"
                  style={{
                    zIndex:"1",
                    marginRight: rightArrowMargin,
                    marginLeft: leftArrowMargin,
                    // the class offset (-12px) balances the list's row spacing; the JSON view has none
                    top: inJsonEditor ? '50%' : undefined
                  }}>
                  <ChevronRightOutlined />
                </span>
              }
              {hasFieldMenu && this.renderFieldMenuButton()}
              {hasFieldMenu && this.renderFieldMenu(type)}
              {(type === 'text' || !type) &&
                <React.Fragment>
                  <Grid size={{ xs: 12, md: 6 }} className={classes.gridOdd} onClick={() => this.selectField(itemId, id)}>
                    <TextField
                      label={(this.props.label && this.props.label.length >= 50) ? <Tooltip title={this.props.label} classes={{ arrow: classes.labelTooltipArrow, popper: classes.labelTooltip, tooltip: classes.noMaxWidth }} placement="bottom" arrow><span>{truncate(this.props.label, 50)}</span></Tooltip> : this.props.label}
                      disabled
                      multiline={this.props.multiline}
                      minRows={this.props.autoGrow ? 1 : this.props.rows}
                      maxRows={this.props.autoGrow ? (this.props.rows || 24) : (this.props.rows ? this.props.rows : 1)}
                      fullWidth
                      value={this.props.source ? this.props.source : ''}
                      margin="none"
                      variant="outlined"
                      InputLabelProps={{
                        style: { pointerEvents: "auto" },
                        shrink: true,
                        classes: {
                          root: classes.label
                        }
                      }}
                      InputProps={{
                        classes: {
                          root: classNames(classes.textField, classes.disabledField, this.props.autoGrow && classes.rootAutoGrow),
                          multiline: classes.textField,
                          input: this.props.autoGrow ? classes.inputFieldAutoGrow : classes.inputField,
                          inputMultiline: this.props.autoGrow ? classes.inputFieldAutoGrow : classes.inputField,
                        }
                      }}
                      inputRef={el => { this.sourceInputEl = el; }}
                      inputProps={{
                        dir: (fromLanguage && fromLanguage.rtl) ? 'rtl' : 'ltr',
                        style: { ...tallFieldStyle(this.props), ...(this.state.pairedMinHeight ? { minHeight: this.state.pairedMinHeight } : {}) },
                      }}
                      className={classNames(classes.textField, classes.targetField)} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }} className={classes.gridEven} onClick={() => this.selectField(itemId, id)} style={{ position: 'relative' }}>
                    {this.currentMode() === 'block' && (
                      <Chip
                        icon={<BlockIcon style={{ fontSize: 14 }} />}
                        label="Blocked"
                        size="small"
                        className={classes.blockedBadge}
                      />
                    )}
                    <TextField
                      multiline={this.props.multiline}
                      minRows={this.props.autoGrow ? 1 : this.props.rows}
                      maxRows={this.props.autoGrow ? (this.props.rows || 24) : (this.props.rows ? this.props.rows : 1)}
                      fullWidth
                      placeholder={
                        (id == 'handle' || (this.props.sourcePlaceholder && !this.state.targetFocused)) && !this.state.usedText
                          ? this.props.source
                          : null
                      }
                      value={this.state.usedText ? this.state.usedText : ''}
                      onChange={this.handleChange}
                      onFocus={() => { if(this.props.sourcePlaceholder) this.setState({ targetFocused: true }); }}
                      onBlur={() => { if(this.props.sourcePlaceholder) this.setState({ targetFocused: false }); }}
                      margin="none"
                      variant="outlined"
                      disabled={this.currentMode() === 'block'}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        classes: {
                          root: classNames(classes.textField, this.currentMode() === 'block' ? classes.disabledField : null, this.props.autoGrow && classes.rootAutoGrow, this.isOutdated() && classes.outdatedInput),
                          multiline: classes.textField,
                          input: this.props.autoGrow ? classes.inputFieldAutoGrow : classes.inputField,
                          inputMultiline: this.props.autoGrow ? classes.inputFieldAutoGrow : classes.inputField,
                        },
                      }}
                      inputRef={el => { this.targetInputEl = el; }}
                      inputProps={{
                        maxLength: this.props.maxLength ? this.props.maxLength : null,
                        dir: (toLanguage && toLanguage.rtl) ? 'rtl' : 'ltr',
                        style: { ...tallFieldStyle(this.props), ...(this.state.pairedMinHeight ? { minHeight: this.state.pairedMinHeight } : {}) },
                      }}
                      // the paper background belongs to the input root (InputProps above), not to the
                      // form control: there it would also paint behind the helper text
                      className={classNames(this.currentMode() && classes.targetFieldWithBadge)}
                      focused={id == this._getFieldIdByHash() && this.currentMode() !== 'block'}
                      error={this._saveFailed()}
                      helperText={this._renderTargetHelper()}
                    />
                    {(this._getCharLimit() && this.state.usedText !== null) &&
                      <Typography variant="caption" className={classNames(classes.maxCharCount, classes[this._charLimitClass(this.state.usedText.length)])}>{`${this.state.usedText.length} of ${this._getCharLimit()} characters used`}</Typography>
                    }
                    {this.hasErrors() !== true && this._handleEqualsSource() &&
                      <Typography variant="caption" className={classes.fieldWarning}>{t('itemsList.handleEqualsOriginal')}</Typography>
                    }
                    {this.hasErrors() !== true && (this._markupIssues() || []).map(issue =>
                      <Typography key={issue} variant="caption" component="p" className={classes.fieldWarning} sx={{ m: 0 }}>{t({ liquid: 'itemsList.markupLiquid', lost: 'itemsList.markupLost', unbalanced: 'itemsList.markupUnbalanced' }[issue])}</Typography>
                    )}
                  </Grid>    

                  {this.props.parentField !== 'json-editor' &&
                    <JsonEditor
                      source={this.props.source}
                      target={this.state.usedText ? this.state.usedText : ''}
                      id={this.props.id}
                      from={this.props.from}
                      to={this.props.to}
                      parentField={this.props.parentField}
                      isPending={this.props.isPending}
                      onChange={this.handleChange}
                      hideButton
                      openRequest={this.state.jsonEditorOpenRequest}
                      onAvailable={available => { if(available !== this.state.jsonEditorAvailable) this.setState({ jsonEditorAvailable: available }); }}
                    />
                  }

                </React.Fragment>
              }
              {type === 'richtext' &&
                <React.Fragment>
                  <Grid size={{ xs: 12, md: 6 }} className={'ly-richtext-editor-source'} onClick={() => this.selectField(itemId, id)}>
                    <RichTextEditor
                      // becomes the editor's #id selector - itemId and id must be CSS-safe, see RichTextEditor
                      id={`${itemId}-${id}-source`}
                      label={this.props.label}
                      version={getEditorVersion()}
                      config={getEditorVersion() === 'v2' ? {
                          editor: {
                            direction: (fromLanguage && fromLanguage.rtl) ? 'rtl' : 'ltr',
                            notranslate: true,
                          },
                          toolbar: false,
                          topbar: false,
                          plugins: ['readonly'],
                          buttons: {
                            addbar: [],
                            context: [],
                            topbar: [],
                          }
                        }
                      :
                        {
                          direction: (fromLanguage && fromLanguage.rtl) ? 'rtl' : 'ltr',
                          buttons: ['html'],
                          plugins: [],
                        }
                      }
                      value={this.props.source}
                      sourceData={this.props.source}
                      isPending={this.props.isPending} 
                      readOnly={true}
                      onLoaded={this.props.onLoaded ? this.props.onLoaded : null}
                      densed
                    />
                  </Grid>             
                  <Grid size={{ xs: 12, md: 6 }} className={classNames('ly-richtext-editor-target', this.isOutdated() && classes.outdatedEditor)} onClick={() => this.selectField(itemId, id)}>
                    <RichTextEditor
                      id={`${itemId}-${id}-target`}
                      version={getEditorVersion()}
                      config={getEditorVersion() === 'v2' ? {
                          placeholder: t('itemsList.richtextPlaceholder'),
                          editor: {
                            direction: (fromLanguage && fromLanguage.rtl) ? 'rtl' : 'ltr',
                            notranslate: true,
                          },
                        }
                      :
                        {
                          placeholder: t('itemsList.richtextPlaceholder'),
                          direction: (toLanguage && toLanguage.rtl) ? 'rtl' : 'ltr',
                          //focus: false,
                        }
                      }
                      triggerDiscardChanges={this.props.triggerDiscardChanges}
                      forceUpdate={this.state.forceUpdate === true ? true : this.props.forceUpdate}
                      sourceData={this.props.source}
                      value={this.state.usedText}
                      isPending={this.props.isPending} 
                      onChange={this.handleChange}
                      onLoaded={this.props.onLoaded ? this.props.onLoaded : null}
                      focused={id == this._getFieldIdByHash()}
                      densed
                      error={this.state.error}
                      helperText={this._renderTargetHelper()}
                    />
                    {(this._getCharLimit() && this.state.usedText) &&
                      <Typography variant="caption" className={classNames(classes.maxCharCount, classes[this._charLimitClass(this.state.usedText.length)])}>{`${this.state.usedText.length} of ${this._getCharLimit()} characters used`}</Typography>
                    }
                  </Grid>
                </React.Fragment>
              } 
            </Grid>
          }
          {(type === 'subfields' && id !== 'seofields' && id !== 'customfields') &&
            <CollapsableFields 
              itemId={this.props.itemId}
              parentField={id} 
              fields={fields}
              type={type}
              id={id}
              label={usedLabel}
              isPending={this.state.isPending} 
              isSubfieldsPending={this.props.isSubfieldsPending}
              isFirst={this.props.isFirst}
              forceUpdate={this.props.forceUpdate}
              forceOpen={this.props.forceOpen}
              triggerClearAllTranslations={this.props.triggerClearAllTranslations}
              triggerDiscardChanges={this.props.triggerDiscardChanges}
              emptyMessage={this.props.emptyMessage}
              from={this.props.from}
              to={this.props.to}
              disabled={(this.props.shop.shop.charge.name === 'free' && id === 'images')}
              locked={(this.props.shop.shop.charge.name === 'free' && id === 'images')}

              onChange={this.handleChange}
              onExpand={this.props.onExpandCollapsableFields}
              onSubfieldSelected={this.props.onSubfieldSelected}
              onSubfieldsExpand={this.props.onSubfieldsExpand}
              onSubfieldsExpanded={this.props.onSubfieldsExpanded}
              onSubfieldsCollapsed={this.props.onSubfieldsCollapsed}

              paginated={paginated && paginated === true}
              totalCount={this.props.totalCount}
              currentPage={this.props.currentPage}
              subfieldsPaginateFunc={this.props.subfieldsPaginateFunc}
            />
          }
          {(type === 'subfields' && id === 'customfields') &&
            <CustomFields 
              itemId={this.props.itemId}
              parentField={id} 
              fields={fields}
              type={type}
              id={id}
              label={usedLabel}
              isPending={this.state.isPending} 
              isFirst={this.props.isFirst}
              forceUpdate={this.props.forceUpdate}
              triggerClearAllTranslations={this.props.triggerClearAllTranslations}
              triggerDiscardChanges={this.props.triggerDiscardChanges}
              emptyMessage={this.props.emptyMessage}
              from={this.props.from}
              to={this.props.to}
              filterQuery={this.props.filterQuery}
              disabled={this.props.shop.shop.charge.name === 'free'}
              locked={this.props.shop.shop.charge.name === 'free'}

              onChange={this.handleChange}
              onExpand={this.props.onExpandCollapsableFields}
              onAddItem={this.props.onAddItem}
              onDeleteItem={this.props.onDeleteItem}
              onSubfieldSelected={this.props.onSubfieldSelected}
              onSubfieldsExpand={this.props.onSubfieldsExpand}
              onSubfieldsExpanded={this.props.onSubfieldsExpanded}
              onSubfieldsCollapsed={this.props.onSubfieldsCollapsed}
            />
          }
          {(type === 'subfields' && id === 'seofields') &&
            <SeoFields
              itemId={this.props.itemId}
              parentField={id} 
              fields={fields}
              type={type}
              id={id}
              label={usedLabel}
              isPending={this.state.isPending} 
              isFirst={this.props.isFirst}
              forceUpdate={this.props.forceUpdate}
              triggerClearAllTranslations={this.props.triggerClearAllTranslations}
              triggerDiscardChanges={this.props.triggerDiscardChanges}
              from={this.props.from}
              to={this.props.to}
              rawData={this.props.rawData}

              onChange={this.handleChange}
              onExpand={this.props.onExpandCollapsableFields}
              onSubfieldSelected={this.props.onSubfieldSelected}
              onSubfieldsExpand={this.props.onSubfieldsExpand}
              onSubfieldsExpanded={this.props.onSubfieldsExpanded}
              onSubfieldsCollapsed={this.props.onSubfieldsCollapsed}
              getFieldIdByHash={this._getFieldIdByHash}
              selectField={this.selectField}
              languages={this.props.languages}
              onLoaded={this.props.onLoaded}

              onTriggerForceUpdate={this.handleTriggerForceUpdate}
              flagsOutdated={this.props.flagsOutdated}
              onKeepTranslation={this.props.onKeepTranslation}
            />
          }
          
          <Snackbar open={loadingTranslationsError} autoHideDuration={6000} onClose={() => this.setState({loadingTranslationsError: false})}>
            <MuiAlert onClose={() => this.setState({loadingTranslationsError: false})} severity="error" elevation={6} variant="filled">
              {t('system.whoopsMessage')}
            </MuiAlert>
          </Snackbar>
        </React.Fragment>
        {this.hasErrors() === true  &&
          <Grid container direction="row-reverse">
            <Grid size={{ xs: 12, md: 6 }} className={classes.gridEven} style={{ padding: "0 12px" }}>
              <Typography variant="caption" className={classNames(classes.informationLine, this._saveFailed() && classes.informationLineFailed)}>{this.state.error_message}</Typography>
            </Grid>
          </Grid>
        }
      </React.Fragment>
    );
  }

  _getFieldIdByHash = () => {
    let hash = window.location.hash.substring(1);
    if(hash.indexOf(':') > -1) {
      hash = hash.split(':')[1];
    }
    return hash;
  }

  // maxLength caps the input, recommendedLength only informs.
  _getCharLimit = () => this.props.maxLength || this.props.recommendedLength;

  // red once an enforced limit is passed, amber for a recommendation - the save only fails on the former
  _charLimitClass = (used) => used <= this._getCharLimit()
    ? null
    : this.props.maxLength ? 'maxCharCountFailed' : 'maxCharCountExceeded';
}
TranslationField.propTypes = {
  //classes: PropTypes.object.isRequired,
  planRestricted: PropTypes.bool,
};
const mapStateToProps = ({ shop, languages }) => ({
  shop: shop,
  languages: languages,
});
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateAutoTranslations
    },
    dispatch
  )

const styles = theme => ({
  root: {
    //backgroundColor: '#ccc !important',
    //paddingLeft: 16,
    position: 'relative !important',
    margin: '0 !important',
    [theme.breakpoints.down('md')]: {
      marginBottom: '24px !important',
    },
    // image rows are bordered boxes with no internal input padding, so they need the gap themselves.
    // Among text rows (theme, size chart) they match those rows' 24px; inside the image groups of
    // products, collections and articles they stack tighter
    '&.image': {
      marginBottom: '24px !important',
    },
    '&.image.grouped': {
      marginBottom: '12px !important',
    },
    // an image with an alt field forms one joined card: square touching corners, no gap between
    '&.image.has-alt': {
      marginBottom: '0 !important',
      // painted above the alt row, which overlaps it by a pixel: the box's bottom border is
      // the shared line and must not vanish under the alt field's background
      position: 'relative',
      zIndex: 1,
      '& .ly-image-field-source, & .ly-image-field-target': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    '&.alt-paired': {
      // pulled up onto the image box's bottom border: the rows are separate grids and a
      // one-pixel seam showed between them on the target side
      marginTop: '-1px !important',
      marginBottom: '0 !important',
      '& .MuiOutlinedInput-root': {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      },
      // the image box above draws the shared line; a second (possibly dashed) border under it
      // read as a one-pixel gap on the target side
      '& .MuiOutlinedInput-notchedOutline': {
        borderTop: 'none',
      },
    },
    '&.richtext': {
      marginBottom: '24px !important',
      [theme.breakpoints.down('md')]: {
        '& .ly-richtext-editor-source': {
          paddingBottom: '0 !important',
          '& .ly-editor > div > div:first-of-type, & .rx-container, & .ly-editor > div .redactor-box': {
            borderBottom: 'none',
            borderBottomLeftRadius: '0 !important',
            borderBottomRightRadius: '0 !important',
          },
        },
        '& .ly-richtext-editor-target': {
          paddingTop: '0 !important',
          '& .ly-editor > div': {
            marginTop: -1,
          },
          '& .ly-editor > div > div:first-of-type, & .rx-container, & .ly-editor > div .redactor-box': {
            borderTopLeftRadius: '0 !important',
            borderTopRightRadius: '0 !important',
          },
        },
      },
    },
  },
  textField: {
    margin: 0,
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4
  },
  inputField: {
    padding: '12px 14px !important',
    fontSize: '14px !important',
    //boxSizing: 'border-box',
    height: 'auto !important',
  },
  // autoGrow fields must keep TextareaAutosize's inline height - the !important override kills it.
  // padding stays on the wrapper (MUI default), NOT on the textarea: padding forced onto the
  // textarea gets double-counted by the autosize measurement and adds a phantom line
  inputFieldAutoGrow: {
    padding: '0 !important',
    fontSize: '14px !important',
  },
  rootAutoGrow: {
    padding: '12px 14px !important',
  },
  targetField: {
    borderStyle: 'dashed !important',
  },
  /**
   * `&&` verdoppelt den Selektor und damit die Spezifität (0,2,0 statt 0,1,0).
   * Nötig, weil diese Regel per `classNames(classes.textField,
   * classes.disabledField)` über die Basisklasse gelegt wird: Emotion
   * schlüsselt Klassen über den REGELINHALT, und dieser hier ist identisch mit
   * `label` weiter unten. Beide teilen sich deshalb eine Klasse — und damit die
   * Sheet-Position des ersten Vorkommens. Solange die vor `textField` liegt,
   * verliert der Modifier still. Das `&&` macht das Ergebnis unabhängig von der
   * Reihenfolge im Objekt und hebt zugleich die Hash-Kollision auf.
   */
  disabledField: {
    '&&': {
      backgroundColor: theme.palette.surfaceRecessed,
    },
  },
  gridItem: {
    margin: '0 !important',
  },
  gridOdd: {
    [theme.breakpoints.down('md')]: {
      paddingBottom: '0 !important',
      '&>div': {
        marginBottom: 0,
        '& fieldset': {
          borderBottom: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }
      },
      '& .ly-editor>div': {
        marginBottom: '0 !important',
        '& > div:first-of-type': {
          borderBottom: 'none',
          borderBottomLeftRadius: '0 !important',
          borderBottomRightRadius: '0 !important',
        },
        '& .redactor-box': {
          borderBottom: 'none',
          borderBottomLeftRadius: '0 !important',
          borderBottomRightRadius: '0 !important',
        }
      },
    },
  },
  gridEven: {
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      paddingTop: '0 !important',
      '&>div': {
        marginTop: -1,
        '& fieldset': {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }
      },
      '& .ly-editor>div': {
        marginTop: -1,
        '& > div:first-of-type': {
          borderTopLeftRadius: '0 !important',
          borderTopRightRadius: '0 !important',
        },
        '& .redactor-box': {
          borderTopLeftRadius: '0 !important',
          borderTopRightRadius: '0 !important',
        }
      },
    },
  },
  arrow: {
    position: 'absolute',
    cursor: 'pointer',
    left: 'calc(50% - 12px)',
    top: 'calc(50% - 12px)',
    transform: 'translateY(-50%)',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    '& svg': {
      //fontSize: 22,
    }
  },
  noTranslationFieldsMsg: {
    textAlign: 'center',
    padding: '50px 0'
  },
  infoField: {
    borderBottom: `solid 1px ${theme.palette.divider}`,
    padding: '8px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    ...bannerSurface(theme, theme.palette.infoBar),
    '& span': {
      lineHeight: 2,
    },
    '& button': {
      color: 'inherit',
      borderColor: 'currentColor',
    }
  },
  warningField: {
    borderBottom: `solid 1px ${theme.palette.divider}`,
    padding: '8px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    ...bannerSurface(theme, theme.palette.warningBar),
    '& span': {
      lineHeight: 2,
    },
    '& button': {
      color: 'inherit',
      borderColor: 'currentColor',
    }
  },
  autoTranslationProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  closeIcon: {
    width: theme.spacing(5),
    height: '100%',
    position: 'absolute',
    cursor: 'pointer',
    right: 4,
    top: 10
  },
  label: {
    backgroundColor: theme.palette.surfaceRecessed,
    borderRadius: 4,
    padding: '0 4px',
  },
  errorLine: {
    /*position: 'absolute',*/
    left: 12,
    bottom: -8,
    color: theme.palette.error.main,
  },
  // sits right under its field: the row above carries the grid's 24px row spacing, which would
  // otherwise leave the message floating far below the input it belongs to
  informationLine: {
    color: theme.palette.text.secondary,
    display: 'block',
    marginTop: -16,
    [theme.breakpoints.down('md')]: {
      marginTop: 0,
    },
  },
  // Only where the translation was rejected - the "adjusted" messages did save and stay neutral
  informationLineFailed: {
    color: theme.palette.error.main,
  },
  // sits where the character counter sits, on the left
  fieldWarning: {
    position: 'absolute',
    left: 12,
    bottom: -20,
    color: theme.palette.warning.main,
  },
  restoreLink: {
    cursor: 'pointer',
    position: 'absolute',
    bottom: -18,
    left: 0,
    zIndex: 1,
  },
  maxCharCount: {
    position: 'absolute',
    right: 12,
    bottom: -20,
    color: theme.palette.text.secondary,
  },
  // doubled selector: emotion keys classes by rule content, so a bare color rule
  // collides with an identical one above and loses the cascade to maxCharCount
  maxCharCountFailed: {
    '&&': { color: theme.palette.error.main },
  },
  maxCharCountExceeded: {
    '&&': { color: theme.palette.warningBar.text },
  },
  noMaxWidth: {
    maxWidth: 'none',
    marginLeft: 50,
  },
  labelTooltip: {
    marginLeft: 50,
  },
  labelTooltipArrow: {
    marginLeft: 50,
  },
  // the source text changed after this translation was written: a warning-coloured outline on
  // the target (no badge - the top-right corner belongs to the character counter)
  outdatedInput: {
    '&& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.warning.main,
      borderWidth: 2,
    },
  },
  outdatedEditor: {
    outline: `2px solid ${theme.palette.warning.main}`,
    outlineOffset: -1,
    borderRadius: 4,
  },
  blockedBadge: {
    position: 'absolute',
    top: -20,
    right: 12,
    zIndex: -1,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 600,
    borderRadius: '8px 8px 0 0',
    '& .MuiChip-icon': {
      color: theme.palette.error.contrastText,
    },
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
    [theme.breakpoints.down('md')]: {
      top: -67,
    }
  },
  targetFieldWithBadge: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
    }
  },
  arrow: {
    position: 'absolute',
    cursor: 'default',
    left: 'calc(50% - 12px)',
    top: 'calc(50% - 12px)',
    transform: 'translateY(-50%)',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('md')]: {
      display: 'none'
    },
    '& svg': {
      //fontSize: 22,
    }
  },
});
export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withTranslation()(TranslationField))));